import React, { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { ChefHat, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/lib/auth-context";
import { useCountry, COUNTRIES } from "@/lib/country-context";
import { useToast } from "@/hooks/use-toast";

export default function RestaurantSigninPage() {
  const { signinRestaurant } = useAuth();
  const { country, setCountry } = useCountry();
  const navigate = useNavigate();
  const location = useLocation();
  const { toast } = useToast();
  const [phoneLocal, setPhoneLocal] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const from = location.state?.from || "/restaurant-portal";
  const dialCode = country.dialCode;
  const setLocal = (raw) => setPhoneLocal((raw || "").replace(/\D/g, ""));

  const submit = async (e) => {
    e.preventDefault();
    if (phoneLocal.length < 6) {
      toast({ title: "Enter a valid phone number", variant: "destructive" });
      return;
    }
    const fullPhone = `${dialCode}${phoneLocal.replace(/^0+/, "")}`;
    setLoading(true);
    try {
      await signinRestaurant(fullPhone, password);
      toast({ title: "Welcome back!" });
      navigate(from, { replace: true });
    } catch (err) {
      toast({
        title: "Sign in failed",
        description: err?.response?.data?.detail || "Invalid phone or password",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background flex items-center justify-center px-6 py-10">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <div className="inline-flex w-12 h-12 rounded-2xl bg-primary text-primary-foreground items-center justify-center mb-6">
            <ChefHat className="w-6 h-6" />
          </div>
          <h1 className="font-serif text-3xl font-bold">Restaurant sign in</h1>
          <p className="text-muted-foreground mt-2">Manage your menu, profile and orders.</p>
        </div>

        <form onSubmit={submit} className="bg-card border rounded-2xl p-6 space-y-4 shadow-sm">
          <div className="space-y-2">
            <Label>Country</Label>
            <div className="grid grid-cols-2 gap-2">
              {["GH", "NG"].map((code) => {
                const c = COUNTRIES[code];
                const active = country.code === code;
                return (
                  <button
                    key={code}
                    type="button"
                    onClick={() => setCountry(code)}
                    data-testid={`r-signin-country-${code}`}
                    className={`flex items-center gap-2 p-2.5 rounded-xl border-2 transition-all text-left text-sm ${
                      active ? "border-primary bg-primary/5" : "border-input hover:border-muted-foreground/40"
                    }`}
                  >
                    <span className="text-xl">{c.flag}</span>
                    <span className="font-semibold">{c.name}</span>
                    <span className="text-xs text-muted-foreground ml-auto">{c.dialCode}</span>
                  </button>
                );
              })}
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="r-phone">Phone number</Label>
            <div className="flex items-stretch h-10 rounded-md border border-input bg-background focus-within:ring-2 focus-within:ring-ring focus-within:ring-offset-2 overflow-hidden">
              <div className="flex items-center gap-1 px-3 bg-muted text-muted-foreground text-sm font-semibold select-none border-r">
                <span className="text-base leading-none">{country.flag}</span>
                <span>{dialCode}</span>
              </div>
              <input
                id="r-phone"
                type="tel"
                inputMode="numeric"
                required
                value={phoneLocal}
                onChange={(e) => setLocal(e.target.value)}
                placeholder={country.code === "GH" ? "24 123 4567" : "801 234 5678"}
                className="flex-1 bg-transparent px-3 text-sm placeholder:text-muted-foreground focus:outline-none"
                data-testid="restaurant-signin-phone"
                autoComplete="tel-national"
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="r-password">Password</Label>
            <Input id="r-password" type="password" required value={password} onChange={(e) => setPassword(e.target.value)}
              placeholder="••••••••" data-testid="restaurant-signin-password" />
          </div>
          <Button type="submit" className="w-full h-11 font-semibold" disabled={loading} data-testid="restaurant-signin-submit">
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Sign in"}
          </Button>
        </form>

        <p className="text-center mt-6 text-sm text-muted-foreground">
          New restaurant on Cravve?{" "}
          <Link to="/restaurant-portal/signup" className="text-primary font-semibold hover:underline" data-testid="restaurant-signup-link">
            Register your restaurant
          </Link>
        </p>
        <p className="text-center mt-3 text-xs text-muted-foreground">
          <Link to="/signin" className="text-muted-foreground hover:underline">← Customer sign in</Link>
        </p>
      </div>
    </div>
  );
}
